import { useMemo, useState } from 'react';
import { CheckCircle2, MessageSquareWarning, ShieldCheck } from 'lucide-react';
import { hasStudioRole } from '../lib/accessApi';
import { saveJourneyReview } from '../lib/trustConfigApi';
import { clinicalJourneyFingerprint } from '../../../src/lib/clinicalJourneyReviewSnapshot.js';

function stageRows(stage) {
  const explanation = stage?.explanation || {};
  const rows = [
    ['Stage summary', stage?.description],
    ['Easy Read text', Array.isArray(stage?.body) ? stage.body.join('\n') : null],
    ['What happens here?', explanation.whatHappens],
    ['Why might this happen?', explanation.whyItMayHappen],
    ['What is the team doing?', explanation.whatTeamIsDoing],
    ['Individualised care', explanation.variationNote],
  ].filter(([, value]) => typeof value === 'string' && value.trim());

  if (stage?.topicIntroduction) rows.push(['Additional guidance', stage.topicIntroduction]);
  for (const topic of Array.isArray(stage?.topics) ? stage.topics : []) {
    const text = (topic.paragraphs || []).filter(Boolean).join('\n');
    if (text) rows.push([topic.title || 'Neonatal information topic', text]);
  }
  if (Array.isArray(explanation.nextSteps) && explanation.nextSteps.length) {
    rows.push(['What might happen next?', explanation.nextSteps
      .map((item) => [item?.title, item?.description, item?.when].filter(Boolean).join(' — '))
      .filter(Boolean)
      .join('\n')]);
  }
  return rows;
}

export default function JourneyClinicalSummaryReview({ trustId, journeyId, journey, reviewSource, reviewRecord, studioAccess, locked, onSaved }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const canReview = hasStudioRole(studioAccess, 'content_reviewer');
  const stages = useMemo(() => journey?.stages || [], [journey]);
  const isCurrent = Boolean(reviewSource && reviewRecord?.contentFingerprint === clinicalJourneyFingerprint(reviewSource));
  const status = isCurrent ? (reviewRecord?.status || 'not_reviewed') : 'updated_review_required';

  const act = async (decision) => {
    if (!canReview || locked || busy) return;
    if (decision === 'changes_requested' && !comment.trim()) {
      setNotice('Add a comment explaining the requested change.');
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const response = await saveJourneyReview(trustId, {
        journeyId,
        decision,
        comment: comment.trim(),
        contentFingerprint: clinicalJourneyFingerprint(reviewSource),
      });
      setComment('');
      setNotice(decision === 'reviewed_accurate' ? 'Clinical content approved.' : 'Change request saved.');
      onSaved?.(response.configuration);
    } catch (error) {
      setNotice(error.message || 'Clinical review could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return <details className="journey-workflow-section journey-clinical-summary">
    <summary>
      <span><strong>1. Review clinical content</strong><small>Read the full patient-facing Journey wording before approval.</small></span>
      <span className={status === 'reviewed_accurate' ? 'journey-status approved' : status === 'changes_requested' ? 'journey-status changes' : 'journey-status'}>{status === 'reviewed_accurate' ? 'Approved' : status === 'changes_requested' ? 'Changes requested' : 'Not reviewed'}</span>
    </summary>
    <div className="journey-workflow-body">
      {journey?.clinicalReleaseHold ? <div className="workflow-lock-banner" role="note"><ShieldCheck/><div><strong>National neonatal clinical review outstanding</strong><span>This is the full generic editorial draft. Your local clinical decision cannot approve or publish the national neonatal text. The Trust publication gate remains closed until national source sign-off is completed.</span></div></div> : null}

      <div className="journey-review-content">
        <div><strong>Journey title and introduction</strong><p>{journey?.title}</p><p>{journey?.introduction || 'No introduction recorded.'}</p></div>
        {stages.map((stage) => <section key={stage.id} className="journey-review-stage">
          <h4>{stage.title}</h4>
          {stageRows(stage).map(([label, value]) => <div key={label}><strong>{label}</strong>{String(value).split('\n').map((line, index) => <p key={`${stage.id}-${label}-${index}`}>{line}</p>)}</div>)}
        </section>)}
        {journey?.templateId === 'neonatal-care' ? <>
          <section className="journey-review-stage">
            <h4>Questions for parents and carers</h4>
            {(journey.questions || []).map((q, i) => <p key={i}>{q}</p>)}
          </section>
          <section className="journey-review-stage">
            <h4>Neonatal glossary: words and plain-language meanings</h4>
            {(journey.neonatalGlossary || []).map((item, i) => <div key={i}><strong>{item.term}</strong><p>{item.meaning}</p></div>)}
          </section>
          <section className="journey-review-stage">
            <h4>Neonatal healthcare role explanations</h4>
            {(journey.neonatalRoleSummaries || []).map((item, i) => <div key={i}><strong>{item.title}</strong><p>{item.description}</p></div>)}
          </section>
          <p>The 16 machine-translation drafts are not professionally signed off; local translation availability remains a separate Trust decision after clinical approval.</p>
        </> : null}
        <section className="journey-review-stage"><h4>Trust links and resources shown to patients</h4>{(journey?.usefulLinks || []).filter(link => link.visible !== false).map(link => <div key={link.id}><strong>{link.title}</strong><p>{link.description || 'No explanatory text'}</p><p>{link.url || 'Trust link not configured'}</p></div>)}</section>
        <section className="journey-review-stage"><h4>Care team shown in this Journey</h4><p>{(journey?.roles || []).map(role => typeof role === 'string' ? role : (role.title || role.name || role.id)).filter(Boolean).join(', ') || 'No roles selected'}</p></section>
        {journey?.templateId === 'waiting-for-surgery' ? <section className="journey-review-stage"><h4>Trust links and escalation routes to approve</h4>{(journey.usefulLinks || []).map((link) => <div key={link.id}><strong>{link.title}</strong><p>{link.visible === false ? 'Not shown to patients' : (link.url || 'Not configured')}</p></div>)}<h4>Questions patients can save</h4>{(journey.questions || []).map((question) => <p key={question}>{question}</p>)}<p>This journey is unlisted. The hospital-team contact must be configured, checked and clinically approved before publication.</p></section> : null}
      </div>

      {reviewRecord?.reviewedAt ? <div className="journey-review-evidence"><strong>Latest clinical review</strong><div><span>{status.replaceAll('_', ' ')}</span><span>{reviewRecord.reviewerEmail || 'Clinical reviewer'}</span>{reviewRecord.comment ? <p>{reviewRecord.comment}</p> : null}</div></div> : null}

      {!isCurrent && reviewRecord?.status === 'reviewed_accurate' && <p role="alert">The Trust Journey content has changed since its last clinical review. A fresh review is required before publication.</p>}
      {canReview ? <div className="journey-review-actions">
        <label>Review comment<textarea rows="3" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Required when requesting changes; optional when approving"/></label>
        <div className="trust-actions">
          <button type="button" className="button primary" disabled={locked || busy} onClick={() => act('reviewed_accurate')}><CheckCircle2 size={16}/>Approve clinical content</button>
          <button type="button" className="button secondary" disabled={locked || busy} onClick={() => act('changes_requested')}><MessageSquareWarning size={16}/>Request changes</button>
        </div>
      </div> : <div className="workflow-lock-banner"><ShieldCheck/><div><strong>Clinical review required</strong><span>A Clinical Reviewer or Platform Admin can approve this wording or request changes. Trust administrators can read the full content here but cannot approve it.</span></div></div>}
      {locked ? <p>This Trust profile is locked while under review.</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
    </div>
  </details>;
}
